import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, User, Users, Flag, CalendarDays, TrendingUp, Handshake, ShieldCheck, Weight, Ruler, Award, Goal, HeartPulse, SquareDot, Target, Share2, Crown } from 'lucide-react';
import useStore from '@/store/useStore';
import { Player, Team } from '@/types/cricket';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';

const PlayerDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { players, teams, fetchPlayers, fetchTeams } = useStore();
  const [player, setPlayer] = useState<Player | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchPlayers();
    fetchTeams();
  }, [fetchPlayers, fetchTeams]);

  useEffect(() => {
    if (players.length > 0 && id) {
      const foundPlayer = players.find(p => p.id === id);
      if (foundPlayer) {
        // Populate team names for display
        const playerTeamNames = foundPlayer.teamIds
          .map(teamId => teams.find(t => t.id === teamId)?.name)
          .filter(Boolean) as string[]; // Filter out undefined and assert type
        setPlayer({ ...foundPlayer, teamNames: playerTeamNames });
      } else {
        setPlayer(null);
      }
    }
  }, [players, id, teams]);

  const handleShareProfile = () => {
    const profileUrl = window.location.href;
    navigator.clipboard.writeText(profileUrl).then(() => {
      toast({
        title: "Link Copied!",
        description: "Player profile link copied to clipboard.",
      });
    }).catch(err => {
      console.error('Failed to copy: ', err);
      toast({
        title: "Error",
        description: "Failed to copy link.",
        variant: "destructive",
      });
    });
  };

  if (!player) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold mb-4">Player not found</h2>
        <Link to="/players">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Players
          </Button>
        </Link>
      </div>
    );
  }

  const roleLabels = {
    'batsman': 'Batsman',
    'bowler': 'Bowler',
    'all-rounder': 'All-Rounder',
    'wicket-keeper': 'Wicket-Keeper',
  };

  const battingStyleLabels = {
    'left-handed': 'Left-handed',
    'right-handed': 'Right-handed',
  };

  const battingOrderLabels = {
    'opener': 'Opener',
    'top-order': 'Top-Order',
    'middle-order': 'Middle-Order',
    'lower-order': 'Lower-Order',
  };

  const bowlingStyleLabels = {
    'fast': 'Fast',
    'medium': 'Medium',
    'spin': 'Spin',
  };

  const bowlingArmLabels = {
    'left-arm': 'Left-Arm',
    'right-arm': 'Right-Arm',
  };

  const bowlingTypeLabels = {
    'pace': 'Pace',
    'off-spin': 'Off-Spin',
    'leg-spin': 'Leg-Spin',
    'left-arm-orthodox': 'Left-Arm Orthodox',
    'left-arm-unorthodox': 'Left-Arm Unorthodox',
  };

  const keepingStyleLabels = {
    'conventional': 'Conventional',
    'aggressive': 'Aggressive',
  };

  const fieldingStyleLabels = {
    'aggressive': 'Aggressive',
    'balanced': 'Balanced',
    'defensive': 'Defensive',
  };

  const injuryStatusLabels = {
    'fit': 'Fit',
    'injured': 'Injured',
    'recovering': 'Recovering',
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in p-4 md:p-8">
      <Button 
        variant="ghost" 
        onClick={() => history.back()} 
        className="mb-4 dark:text-gray-100"
      >
            <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Players
          </Button>

      <Card className="shadow-xl rounded-2xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 bg-white">
        <CardContent className="p-0">
          {/* Cover Image */}
          <div className="relative w-full h-32 bg-gradient-to-r from-cricket-green to-cricket-accent flex items-center justify-center dark:from-cricket-green-dark dark:to-cricket-accent-dark rounded-t-2xl">
            {player.coverImageUrl ? (
              <img
                src={player.coverImageUrl}
                alt={`${player.name}'s Cover`}
                className="w-full h-full object-cover rounded-t-2xl"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-600 rounded-t-2xl">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-16 h-16"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5 1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008v-.008zm.75 0h.008v.008h-.008v-.008z"
                  />
                </svg>
              </div>
            )}
            {/* Profile Picture */}
            <div className="absolute -bottom-16 left-8 group z-10">
              <Avatar className="w-32 h-32 border-4 border-white shadow-lg group-hover:scale-105 transition-transform duration-200">
                {player.profilePictureUrl ? (
                  <AvatarImage src={player.profilePictureUrl} alt={`${player.name}'s Profile`} />
                ) : (
                  <AvatarFallback 
                    className="text-5xl font-bold text-white flex items-center justify-center uppercase"
                    style={{ backgroundColor: player.logoColor }}
                  >
                    {player.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                )}
              </Avatar>
            </div>
      </div>

          {/* Player Info */}
          <div className="pt-20 px-8 pb-8 space-y-4 dark:bg-gray-900 bg-white">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-4xl font-extrabold text-cricket-green dark:text-cricket-green-light flex items-center gap-2">
                  {player.name}
                  {player.role && (
                    <span className="ml-2 px-3 py-1 rounded-full bg-cricket-accent text-white text-sm font-semibold shadow-sm animate-fade-in flex items-center gap-1 dark:bg-cricket-accent-dark">
                      <User className="w-4 h-4" />
                      {roleLabels[player.role]}
                    </span>
                  )}
                </h1>
                {player.country && <p className="text-gray-600 dark:text-gray-300 flex items-center gap-2 mt-1"><Flag className="w-4 h-4 text-cricket-green dark:text-cricket-green-light" />{player.country}</p>}
                {player.teamNames && player.teamNames.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 text-gray-600 dark:text-gray-300 mt-2">
                    <Users className="w-4 h-4 text-cricket-green dark:text-cricket-green-light" />
                    <span>Teams: </span>
                    {player.teamNames.map((teamName, index) => (
                      <span
                        key={teamName}
                        className="font-bold px-3 py-1 rounded bg-green-100 text-green-800 shadow-sm border border-green-300 hover:bg-green-200 transition-colors cursor-pointer dark:bg-green-900 dark:text-green-200 dark:border-green-700"
                      >
                        {teamName}{index < player.teamNames.length - 1 ? ',' : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleShareProfile}
                className="flex items-center gap-2 border-cricket-green text-cricket-green hover:bg-cricket-green hover:text-white transition-colors dark:border-cricket-green-light dark:text-cricket-green-light dark:hover:bg-cricket-green-light dark:hover:text-gray-900"
                title="Copy profile link"
              >
                <Share2 className="mr-2 h-4 w-4" />
                Share Profile
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-8">
        {/* Bio Data Card */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md border border-gray-100 dark:border-gray-700 p-6 transition-transform duration-200 hover:shadow-lg hover:-translate-y-1 animate-fade-in w-full lg:col-span-2">
          <div className="flex items-center gap-2 mb-4 border-l-4 border-cricket-green dark:border-cricket-green-light pl-2">
            <User className="w-5 h-5 text-cricket-green dark:text-cricket-green-light" />
            <span className="text-xl font-bold text-gray-900 dark:text-gray-100">Bio Data</span>
          </div>
          <div className="space-y-3">
            {player.dateOfBirth && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-cricket-green dark:text-cricket-green-light" />Date of Birth</span>
                <span className="font-semibold dark:text-gray-100">{new Date(player.dateOfBirth).toLocaleDateString()}</span>
              </div>
            )}
            {player.placeOfBirth && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2"><Flag className="w-4 h-4 text-cricket-green dark:text-cricket-green-light" />Place of Birth</span>
                <span className="font-semibold dark:text-gray-100">{player.placeOfBirth}</span>
              </div>
            )}
            {player.nationality && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2"><Flag className="w-4 h-4 text-cricket-green dark:text-cricket-green-light" />Nationality</span>
                <span className="font-semibold dark:text-gray-100">{player.nationality}</span>
              </div>
            )}
            {player.gender && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2"><User className="w-4 h-4 text-cricket-green dark:text-cricket-green-light" />Gender</span>
                <span className="font-semibold capitalize dark:text-gray-100">{player.gender}</span>
              </div>
            )}
            {player.heightCm && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2"><Ruler className="w-4 h-4 text-cricket-green dark:text-cricket-green-light" />Height</span>
                <span className="font-semibold dark:text-gray-100">{player.heightCm} cm</span>
              </div>
            )}
            {player.weightKg && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2"><Weight className="w-4 h-4 text-cricket-green dark:text-cricket-green-light" />Weight</span>
                <span className="font-semibold dark:text-gray-100">{player.weightKg} kg</span>
              </div>
            )}
            {player.jerseyNumber && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2"><Award className="w-4 h-4 text-cricket-green dark:text-cricket-green-light" />Jersey Number</span>
                <span className="font-semibold dark:text-gray-100">#{player.jerseyNumber}</span>
              </div>
            )}
            {player.nickname && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2"><User className="w-4 h-4 text-cricket-green dark:text-cricket-green-light" />Nickname</span>
                <span className="font-semibold dark:text-gray-100">{player.nickname}</span>
              </div>
            )}
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2">
                <span className="inline-block bg-gray-100 dark:bg-gray-800 rounded-full p-1"><Crown className="w-4 h-4 text-yellow-500" /></span>
                Team Captain
              </span>
              {player.isCaptain ? (
                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-700 border border-green-200 dark:bg-green-900 dark:text-green-200 dark:border-green-700">Yes</span>
              ) : (
                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-700 border border-red-200 dark:bg-red-900 dark:text-red-200 dark:border-red-700">No</span>
              )}
            </div>
          </div>
        </div>
        {/* Cricket Performance & Health Card */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md border border-gray-100 dark:border-gray-700 p-6 transition-transform duration-200 hover:shadow-lg hover:-translate-y-1 animate-fade-in w-full lg:col-span-3">
          <div className="flex items-center gap-2 mb-4 border-l-4 border-cricket-accent dark:border-cricket-accent-dark pl-2">
            <TrendingUp className="w-5 h-5 text-cricket-accent dark:text-cricket-accent-dark" />
            <span className="text-xl font-bold text-gray-900 dark:text-gray-100">Cricket Performance & Health</span>
          </div>
          <div className="flex-1">
            {/* Batting Stats */}
            {(player.role === 'batsman' || player.role === 'all-rounder') && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-cricket-accent dark:text-cricket-accent-dark flex items-center gap-2 mb-2"><Award className="w-5 h-5" />Batting</h3>
                <div className="space-y-2">
                  {player.battingStyle && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Style</span>
                      <span className="font-semibold capitalize dark:text-gray-100">{battingStyleLabels[player.battingStyle]}</span>
                    </div>
                  )}
                  {player.battingOrder && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Order</span>
                      <span className="font-semibold capitalize dark:text-gray-100">{battingOrderLabels[player.battingOrder]}</span>
                    </div>
                  )}
                  {player.careerRuns !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Career Runs</span>
                      <span className="font-semibold dark:text-gray-100">{player.careerRuns}</span>
                    </div>
                  )}
                  {player.careerMatchesPlayed !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Matches Played</span>
                      <span className="font-semibold dark:text-gray-100">{player.careerMatchesPlayed}</span>
                    </div>
                  )}
                  {player.careerInningsPlayed !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Innings Played</span>
                      <span className="font-semibold dark:text-gray-100">{player.careerInningsPlayed}</span>
                    </div>
                  )}
                  {player.careerStrikeRate !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Strike Rate</span>
                      <span className="font-semibold dark:text-gray-100">{player.careerStrikeRate.toFixed(2)}</span>
                    </div>
                  )}
                  {player.careerAverage !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Average</span>
                      <span className="font-semibold dark:text-gray-100">{player.careerAverage.toFixed(2)}</span>
                    </div>
                  )}
                  {player.highestScore !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Highest Score</span>
                      <span className="font-semibold dark:text-gray-100">{player.highestScore}</span>
                    </div>
                  )}
                  {player.notOuts !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Not Outs</span>
                      <span className="font-semibold dark:text-gray-100">{player.notOuts}</span>
                    </div>
                  )}
                  {player.fours !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Fours</span>
                      <span className="font-semibold dark:text-gray-100">{player.fours}</span>
                    </div>
                  )}
                  {player.sixes !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Sixes</span>
                      <span className="font-semibold dark:text-gray-100">{player.sixes}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Bowling Stats */}
            {(player.role === 'bowler' || player.role === 'all-rounder') && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-cricket-accent dark:text-cricket-accent-dark flex items-center gap-2 mb-2"><Award className="w-5 h-5" />Bowling</h3>
                <div className="space-y-2">
                  {player.bowlingStyle && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Style</span>
                      <span className="font-semibold capitalize dark:text-gray-100">{bowlingStyleLabels[player.bowlingStyle]}</span>
                    </div>
                  )}
                  {player.bowlingArm && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Arm</span>
                      <span className="font-semibold capitalize dark:text-gray-100">{bowlingArmLabels[player.bowlingArm]}</span>
                    </div>
                  )}
                  {player.bowlingType && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Type</span>
                      <span className="font-semibold capitalize dark:text-gray-100">{bowlingTypeLabels[player.bowlingType]}</span>
                    </div>
                  )}
                  {player.careerWickets !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Career Wickets</span>
                      <span className="font-semibold dark:text-gray-100">{player.careerWickets}</span>
                    </div>
                  )}
                  {player.careerOversBowled !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Overs Bowled</span>
                      <span className="font-semibold dark:text-gray-100">{player.careerOversBowled.toFixed(1)}</span>
                    </div>
                  )}
                  {player.careerEconomyRate !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Economy Rate</span>
                      <span className="font-semibold dark:text-gray-100">{player.careerEconomyRate.toFixed(2)}</span>
                    </div>
                  )}
                  {player.careerBowlingAverage !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Bowling Average</span>
                      <span className="font-semibold dark:text-gray-100">{player.careerBowlingAverage.toFixed(2)}</span>
                    </div>
                  )}
                  {player.bestBowlingFigures && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Best Figures</span>
                      <span className="font-semibold dark:text-gray-100">{player.bestBowlingFigures}</span>
                    </div>
                  )}
                  {player.maidenOvers !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Maiden Overs</span>
                      <span className="font-semibold dark:text-gray-100">{player.maidenOvers}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Fielding & Keeping Stats */}
            {(player.role === 'wicket-keeper' || player.fieldingPosition || player.fieldingStyle) && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-cricket-accent dark:text-cricket-accent-dark flex items-center gap-2 mb-2"><Award className="w-5 h-5" />Fielding & Keeping</h3>
                <div className="space-y-2">
                  {player.fieldingPosition && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Position</span>
                      <span className="font-semibold capitalize dark:text-gray-100">{player.fieldingPosition}</span>
                    </div>
                  )}
                  {player.fieldingStyle && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Style</span>
                      <span className="font-semibold capitalize dark:text-gray-100">{fieldingStyleLabels[player.fieldingStyle]}</span>
                    </div>
                  )}
                  {player.keepingStyle && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Keeping Style</span>
                      <span className="font-semibold capitalize dark:text-gray-100">{keepingStyleLabels[player.keepingStyle]}</span>
                    </div>
                  )}
                  {player.catches !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Catches</span>
                      <span className="font-semibold dark:text-gray-100">{player.catches}</span>
                    </div>
                  )}
                  {player.runOuts !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Run Outs</span>
                      <span className="font-semibold dark:text-gray-100">{player.runOuts}</span>
                    </div>
                  )}
                  {player.stumpings !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Stumpings</span>
                      <span className="font-semibold dark:text-gray-100">{player.stumpings}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* If no stats available, show message */}
            {!(
              (player.role === 'batsman' || player.role === 'all-rounder') &&
                (player.battingStyle || player.battingOrder || player.careerRuns !== undefined || player.careerMatchesPlayed !== undefined || player.careerInningsPlayed !== undefined || player.careerStrikeRate !== undefined || player.careerAverage !== undefined || player.highestScore !== undefined || player.notOuts !== undefined || player.fours !== undefined || player.sixes !== undefined) ||
              (player.role === 'bowler' || player.role === 'all-rounder') &&
                (player.bowlingStyle || player.bowlingArm || player.bowlingType || player.careerWickets !== undefined || player.careerOversBowled !== undefined || player.careerEconomyRate !== undefined || player.careerBowlingAverage !== undefined || player.bestBowlingFigures || player.maidenOvers !== undefined) ||
              (player.role === 'wicket-keeper' || player.fieldingPosition || player.fieldingStyle) &&
                (player.fieldingPosition || player.fieldingStyle || player.keepingStyle || player.catches !== undefined || player.runOuts !== undefined || player.stumpings !== undefined)
            ) && (
              <div className="flex-1 flex items-center justify-center">
                <span className="text-gray-400 text-center">No specific cricket performance data available for this role.</span>
              </div>
            )}
          </div>
        </div>
        {/* Add more cards as needed */}
      </div>
    </div>
  );
};

export default PlayerDetailPage;
